import {t} from 'i18next';
import {useState} from 'react';
import {CreateBooking, DisplayMessage, WeddingOccasionDB, WeddingOccasionDTO} from '../lib/interfaces';
import {useBookingState} from '../store';
import {ApiErrorMessages, RoutePath} from '../lib/enums';
import {useNavigate} from 'react-router-dom';
import {formatWeddingOccasionTime} from '../helpers';
import {useUserClaims} from '../hooks/useUserClaims';
import {UPPSALA_KOMMUN_PHONE_NUMBER, pinUserClaim} from '../utils/constants';
import {createBooking} from '../services/bookingServices';
import {getFirstAvailable} from '../services/weddingOccasionServices';
import {Trans} from 'react-i18next';
import {UaButton, UaDialog} from 'uppsala.ui.react';

const ConfirmBooking = () => {
  const navigate = useNavigate();
  const state = useBookingState((state) => state);
  const {userClaims} = useUserClaims();

  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [bookingResult, setBookingResult] = useState<DisplayMessage | null>(null);
  const [suggestedOccasion, setSuggestedOccasion] = useState<WeddingOccasionDTO | null>(null);

  function getDialogContent() {
    if (!bookingResult) return;

    if (bookingResult.message === ApiErrorMessages.PERSON_ERROR) {
      return (
        <UaDialog
          visible={visible}
          title={t('message.bookingPersonErrorHeader')}
          cancelText={t('button.close')}
          onCancel={() => setVisible(false)}
          content={<p style={{whiteSpace: 'pre-line'}}>{t('message.bookingPersonError')}</p>}
        />
      );
    }

    // This person already has an occasion booked
    if (bookingResult.message === ApiErrorMessages.DUPLICATE_BOOKING) {
      // TODO: Get bookingResult.message and set dialogMessage to the right translation string
      const dialogMessage = t('message.duplicateBookingMessage');
      return (
        <UaDialog
          visible={visible}
          title={t('message.undefinedErrorHeader')}
          content={<p style={{whiteSpace: 'pre-line'}}>{dialogMessage}</p>}
          okText={t('booking.myPages')}
          onOk={() => navigate(RoutePath.MyPages)}
        />
      );
    }

    // The occasion was already booked by someone else
    if (bookingResult.message === ApiErrorMessages.OCCASION_NOT_AVAILABLE && suggestedOccasion) {
      return (
        <UaDialog
          visible={visible}
          title={t('message.occasionUnavailableHeader')}
          content={
            <>
              <p>{t('message.occasionUnavailable')}</p>
              <p style={{marginTop: '15px', fontWeight: 'bold'}}>{t('message.suggestNewOccasion')}</p>
              <p style={{marginTop: '5px'}}>
                {formatWeddingOccasionTime(suggestedOccasion.time, suggestedOccasion.weddingOfficiantName)}
              </p>
            </>
          }
          cancelText={t('button.selectAnotherTime')}
          onCancel={() => {
            state.setSelectedMonthYear('');
            state.setWeddingOccasionId('');
            navigate(RoutePath.HomePage);
          }}
          okText={t('button.bookNewTime')}
          onOk={() => {
            // Update the state with the suggested occasion since it will be used in the confirmation page
            state.setWeddingOccasion({
              id: suggestedOccasion.id,
              location: suggestedOccasion.location,
              time: suggestedOccasion.time,
              weddingOfficiant: {name: suggestedOccasion.weddingOfficiantName},
            } as WeddingOccasionDB);
            confirmBooking(suggestedOccasion.id);
            setVisible(false);
          }}
        />
      );
    }

    return (
      <UaDialog
        visible={visible}
        title={t('message.undefinedErrorHeader')}
        content={
          <p className="whitespace-pre-line">
            <Trans
              i18nKey={'message.undefinedErrorMessage'}
              values={{number: UPPSALA_KOMMUN_PHONE_NUMBER}}
              components={{
                1: <a href={`tel:${UPPSALA_KOMMUN_PHONE_NUMBER}`} aria-label="Telefonnummer" />,
              }}
            />
          </p>
        }
        cancelText={t('button.close')}
        onCancel={() => setVisible(false)}
      />
    );
  }

  async function confirmBooking(suggestedOccasionId?: string) {
    if (loading) return;
    setLoading(true);
    const result = await createBooking({
      actor: userClaims?.[pinUserClaim] ?? '',
      isCustomOccasion: state.isCustomOccasion,
      person1: state.person1,
      person2: state.person2,
      ceremonyLanguage: state.ceremonyLanguage,
      weddingOccasionId: suggestedOccasionId ?? state.weddingOccasionId,
      customBookingCreateDetails: {
        weddingOfficiantId: state.weddingOfficiantId,
        city: state.customOccasionCity,
        address: state.customOccasionAddress,
        time: state.weddingOccasionTime,
      },
    } as CreateBooking);

    if (result.data) {
      navigate(RoutePath.ConfirmationPage);
      return;
    }

    if (result.info?.message === ApiErrorMessages.OCCASION_NOT_AVAILABLE) {
      const result = await getFirstAvailable(state.weddingOccasionTime.split('T')[0]);
      setSuggestedOccasion(result.data);
    }

    setBookingResult(result.info);
    setLoading(false);
    setVisible(true);
    window.scrollTo(0, 0);
  }

  return (
    <>
      {getDialogContent()}
      <UaButton type="primary" title={t('button.confirmBooking')} loading={loading} onClick={() => confirmBooking()} />
    </>
  );
};

export default ConfirmBooking;
